import { usePrevious } from '@mantine/hooks';
import { LOGIN_TYPE } from 'consts';
import { authMethodPriority } from 'consts/authMethodPriority';
import { LOGIN_DEFAULT_TYPE } from 'consts/loginType';
import { useGetBoardInvitation } from 'entities/board/api/hooks';
import { useGetNotificationHint } from 'entities/notification/api/hint';
import { useGetAuthLoginMethods } from 'features/auth/api';
import { useGetAuthHint } from 'features/auth/api/hint';
import { useGetAuth } from 'features/document-invitation/api/otp';
import { getLoginMethodsObject } from 'helpers';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Error } from 'shared/ui/Error';
import type { LoginMethodType } from 'types';
import { NonLoggedLayout } from 'widgets/NonLoggedLayout';
import { PromotionalCarousel } from 'widgets/promotional-carousel';
import { Login } from './Login';
import { DeletedAccountModal } from './Login/DeletedAccountModal';

const getNotificationIdFromUrl = (
  searchParams: URLSearchParams,
): string | undefined => {
  const directNotificationId = searchParams.get('notificationId');
  if (directNotificationId) {
    return directNotificationId;
  }

  const redirectTo = searchParams.get('redirectTo');
  if (redirectTo?.includes('?')) {
    const redirectParams = new URLSearchParams(redirectTo.split('?')[1]);
    return redirectParams.get('notificationId') || undefined;
  }

  return undefined;
};

const getHintTokenFromUrl = (
  searchParams: URLSearchParams,
): string | undefined => {
  const directHintToken = searchParams.get('hint');
  if (directHintToken) {
    return directHintToken;
  }

  const redirectTo = searchParams.get('redirectTo');
  if (redirectTo?.includes('?')) {
    const redirectParams = new URLSearchParams(redirectTo.split('?')[1]);
    return redirectParams.get('hint') || undefined;
  }

  return undefined;
};

export const Auth = () => {
  // Router hooks
  const location = useLocation();
  const navigate = useNavigate();
  const { invitationId } = useParams();
  const previousInvitationId = usePrevious(invitationId);

  // URL params
  const searchParams = new URLSearchParams(location.search);
  const organisationInvitationToken = searchParams.get(
    'organisation_invitation_token',
  );
  const notificationId = getNotificationIdFromUrl(searchParams);
  const hintToken = getHintTokenFromUrl(searchParams);

  // Route flags
  const isOnDocumentInvitationRoute = Boolean(invitationId);
  const isOnBoardInvitationRoute = Boolean(organisationInvitationToken);
  const isOnNotificationRoute = Boolean(notificationId);
  const isOnHintRoute = Boolean(hintToken);

  // Local state
  const [loginMethod, setLoginMethod] = useState<LoginMethodType>();
  const [loginMethods, setLoginMethods] = useState<{
    [key in keyof typeof LOGIN_TYPE]: boolean;
  }>({});

  const isAccountDeleted = Boolean(location.state?.isAccountDeleted);
  const [isDeletedAccountModalOpen, setIsDeletedAccountModalOpen] =
    useState(isAccountDeleted);

  // API calls for invitations
  const {
    data: invitationData,
    isError: isInvitationLoginMethodsError,
    isInitialLoading: isInvitationLoginMethodsInitialLoading,
  } = useGetAuth(
    { pathParams: { invitationId: invitationId as string } },
    { enabled: isOnDocumentInvitationRoute },
  );

  const {
    data: { email: organisationInvitationEmail } = {},
    isInitialLoading: isGetBoardInvitationInitialLoading,
  } = useGetBoardInvitation(
    { pathParams: { invitationId: organisationInvitationToken || '' } },
    { enabled: isOnBoardInvitationRoute },
  );

  const {
    data: notificationData,
    isInitialLoading: isGetNotificationHintInitialLoading,
    isError: isNotificationHintError,
  } = useGetNotificationHint(
    { pathParams: { notificationId: notificationId || '' } },
    { enabled: isOnNotificationRoute },
  );

  const {
    data: authHintData,
    isInitialLoading: isGetAuthHintInitialLoading,
    isError: isAuthHintError,
  } = useGetAuthHint(
    { pathParams: { hintToken: hintToken || '' } },
    { enabled: isOnHintRoute },
  );

  // Derived data from invitations
  const email =
    invitationData?.email ||
    organisationInvitationEmail ||
    notificationData?.email ||
    authHintData?.email;

  const phone =
    invitationData?.phone || notificationData?.phone || authHintData?.phone;

  const {
    data: { loginMethods: authLoginMethodsFromApi } = {},
    mutate: getAuthLoginMethods,
  } = useGetAuthLoginMethods();

  const authLoginMethods =
    invitationData?.loginMethods ||
    (!isNotificationHintError && notificationData?.loginMethods) ||
    (!isAuthHintError && authHintData?.loginMethods) ||
    authLoginMethodsFromApi;

  // Determine identifier based on available login methods
  const hasSmsMethod = authLoginMethods?.includes(LOGIN_TYPE.sms_code);
  const hasEmailMethod = authLoginMethods?.includes(LOGIN_TYPE.email_code);

  const identifier =
    (hasSmsMethod && phone) || (hasEmailMethod && email) || phone || email;

  // Helper functions
  const resetLoginMethods = () => {
    setLoginMethod(undefined);
    setLoginMethods({});
  };

  const extractAndSetLoginMethods = (loginMethodsArray?: LoginMethodType[]) => {
    const methods = getLoginMethodsObject(loginMethodsArray);
    const method = authMethodPriority.find(
      (methodName: LoginMethodType) => methods[methodName],
    );

    setLoginMethods(methods);
    setLoginMethod((method as LoginMethodType) || LOGIN_DEFAULT_TYPE);
  };

  const switchLoginMethod = () => {
    setLoginMethod(previousLoginMethod => {
      if (
        previousLoginMethod === LOGIN_TYPE.email_code ||
        previousLoginMethod === LOGIN_TYPE.sms_code
      ) {
        return LOGIN_TYPE.password;
      }

      return loginMethods[LOGIN_TYPE.email_code]
        ? LOGIN_TYPE.email_code
        : LOGIN_TYPE.sms_code;
    });
  };

  const hasNavigatedFromInvitationToHome =
    invitationId === undefined && previousInvitationId;

  // Effects
  useEffect(() => {
    const shouldFetchLoginMethods =
      identifier &&
      !invitationData &&
      (!notificationData || isNotificationHintError) &&
      (!authHintData || isAuthHintError);

    if (shouldFetchLoginMethods) {
      const isPhone = !identifier!.includes('@');
      getAuthLoginMethods(
        isPhone ? { phone: identifier! } : { email: identifier! },
      );
    }
  }, [
    identifier,
    getAuthLoginMethods,
    invitationData,
    notificationData,
    isNotificationHintError,
    authHintData,
    isAuthHintError,
  ]);

  useEffect(() => {
    if (authLoginMethods) {
      extractAndSetLoginMethods(authLoginMethods);
    }
  }, [authLoginMethods]);

  useEffect(() => {
    if (hasNavigatedFromInvitationToHome) {
      resetLoginMethods();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasNavigatedFromInvitationToHome]);

  useEffect(() => {
    if (isAccountDeleted) {
      navigate('/', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAccountDeleted]);

  // Computed values for UI
  const isOnValidNotificationRoute =
    isOnNotificationRoute && !isNotificationHintError;

  const isOnValidHintRoute = isOnHintRoute && !isAuthHintError;

  const isOnInvitationRoute =
    isOnDocumentInvitationRoute ||
    isOnBoardInvitationRoute ||
    isOnValidNotificationRoute ||
    isOnValidHintRoute;

  const isLoginViewLoading =
    isOnInvitationRoute &&
    (isInvitationLoginMethodsInitialLoading ||
      isGetBoardInvitationInitialLoading ||
      isGetNotificationHintInitialLoading ||
      isGetAuthHintInitialLoading ||
      !loginMethod);

  const backButtonAction =
    !isOnInvitationRoute && loginMethod && !hasNavigatedFromInvitationToHome
      ? resetLoginMethods
      : undefined;

  // Render
  if (isInvitationLoginMethodsError) {
    return (
      <NonLoggedLayout
        primaryContent={<PromotionalCarousel />}
        secondaryContent={<Error />}
        isSecondaryContentLoading={false}
      />
    );
  }

  return (
    <>
      <NonLoggedLayout
        primaryContent={<PromotionalCarousel />}
        secondaryContent={
          <Login
            loginMethod={loginMethod}
            loginMethods={loginMethods}
            backButtonAction={loginMethod && resetLoginMethods}
            switchLoginMethod={switchLoginMethod}
            extractAndSetLoginMethods={extractAndSetLoginMethods}
            identifier={identifier || undefined}
            isOnDocumentInvitationRoute={isOnDocumentInvitationRoute}
            isOnBoardInvitationRoute={isOnBoardInvitationRoute}
            isOnNotificationRoute={isOnValidNotificationRoute}
            isOnHintRoute={isOnValidHintRoute}
          />
        }
        isSecondaryContentLoading={isLoginViewLoading}
        backButtonAction={backButtonAction}
      />
      <DeletedAccountModal
        isOpen={isDeletedAccountModalOpen}
        onClose={() => setIsDeletedAccountModalOpen(false)}
      />
    </>
  );
};
